/**
 * Step 2 — Source configuration.
 *
 * Renders the selected capability's `config_schema.fields[]` as form inputs
 * via FieldRenderer. Form values flow into wizard state via
 * `update_source_config`. "Continue" validates required fields before
 * advancing; "Back" returns to Step 1, where the reducer preserves
 * source_config if the user re-selects the same capability.
 *
 * For empty schemas (a hypothetical capability with no configurable fields)
 * the step renders a friendly "nothing to configure" placeholder and lets
 * the user proceed without input.
 */
import React, { useMemo } from 'react';

import { Button } from '../../../ui/button';
import type {
  CapabilityField,
  ImportCapability,
  ImportCapabilitySkill,
} from '../../../../types/importCapabilities';
import FieldRenderer from './fields/FieldRenderer';

interface SourceConfigStepProps {
  skill: ImportCapabilitySkill;
  capability: ImportCapability;
  values: Record<string, unknown>;
  onChange: (values: Record<string, unknown>) => void;
  onBack: () => void;
  onContinue: () => void;
}

/**
 * Returns the field ids that are declared `required: true` AND missing
 * a non-empty value in the current form state. Empty arrays count as
 * missing for multi_select; null / undefined / "" count as missing for
 * everything else.
 */
const findMissingRequired = (
  fields: CapabilityField[],
  values: Record<string, unknown>,
): string[] => {
  return fields
    .filter((f) => f.required)
    .filter((f) => {
      const v = values[f.id];
      if (v === undefined || v === null || v === '') return true;
      if (Array.isArray(v) && v.length === 0) return true;
      return false;
    })
    .map((f) => f.id);
};

const SourceConfigStep: React.FC<SourceConfigStepProps> = ({
  skill,
  capability,
  values,
  onChange,
  onBack,
  onContinue,
}) => {
  const fields = capability.config_schema?.fields ?? [];

  const missingRequired = useMemo(
    () => findMissingRequired(fields, values),
    [fields, values],
  );
  const canContinue = missingRequired.length === 0;

  if (fields.length === 0) {
    return (
      <div className="space-y-4">
        <p className="rounded-md border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
          This source has no configurable options. Click Continue to pick a
          destination.
        </p>
        <div className="flex justify-between">
          <Button variant="outline" onClick={onBack}>
            Back
          </Button>
          <Button onClick={onContinue}>Continue</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <h2 className="text-lg font-semibold text-slate-900">Configure {skill.name || skill.skill_name}</h2>
        <p className="text-sm text-slate-600">
          Set the options for this monitor. Required fields are marked with{' '}
          <span className="text-red-500">*</span>.
        </p>
      </header>

      <div className="space-y-5 rounded-lg border border-slate-200 bg-white p-4">
        {fields.map((field) => (
          <FieldRenderer
            key={field.id}
            field={field}
            value={values[field.id]}
            onChange={(v) => {
              // When a field changes, clear any DEPENDENT field whose
              // options resolver consumes this id — otherwise its old
              // value (e.g. ``brand_id`` picked under account A) stays
              // selected even though the brand dropdown has just
              // refreshed to a different account's brands, leading to
              // a mismatched Save payload.
              const patch: Record<string, unknown> = { [field.id]: v };
              for (const f of fields) {
                const deps = f.options_provider?.depends_on;
                if (!deps || !deps.includes(field.id)) continue;
                if (f.id === field.id) continue;
                // Only clear if something was actually set; avoids
                // touching unmounted fields and keeps the patch small.
                if (values[f.id] !== undefined) {
                  patch[f.id] = undefined;
                }
              }
              onChange(patch);
            }}
            skillName={skill.skill_name}
            capabilityId={capability.capability_id}
            context={values}
          />
        ))}
      </div>

      <div className="flex items-center justify-between">
        <Button variant="outline" onClick={onBack}>
          Back
        </Button>
        <div className="flex items-center gap-3">
          {!canContinue ? (
            <span className="text-xs text-slate-500">
              {missingRequired.length} required field
              {missingRequired.length === 1 ? '' : 's'} left
            </span>
          ) : null}
          <Button onClick={onContinue} disabled={!canContinue}>
            Continue
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SourceConfigStep;
